import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ClipboardCheck, AlertCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { VISA_TYPES, COUNTRIES, EDUCATION_LEVELS, EXPERIENCE_LEVELS, ENGLISH_STATUS, ELIGIBILITY_DISCLAIMER, IMAGES } from "../data/content";
import { Reveal } from "../lib/motion";
import { t } from "../i18n";

const EMPTY = {
  name: "", age: "", phone: "", email: "", education: "", occupation: "",
  experience: "", country: "", visa_type: "", english_status: "", message: "",
};

const Select = ({ label, value, onChange, options, testid }) => (
  <select required value={value} onChange={onChange} data-testid={testid} className="input-premium appearance-none">
    <option value="" disabled className="bg-navy">{label}</option>
    {options.map((o) => (
      <option key={o} value={o} className="bg-navy">{o}</option>
    ))}
  </select>
);

const EligibilitySection = ({ compact = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/eligibility", { ...form, age: Number(form.age) });
      setDone(true);
      setForm(EMPTY);
      toast.success("Eligibility request received. Our team will review your profile.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="eligibility" data-testid="eligibility-section" className={`relative bg-navy-deep overflow-hidden ${compact ? "" : "py-24 lg:py-32"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-14 items-center ${compact ? "" : ""}`}>
          <Reveal className={compact ? "hidden lg:block" : ""}>
            <div className="relative rounded-2xl overflow-hidden h-full min-h-[540px] hidden lg:block">
              <img src={IMAGES.eligibility} alt="View from an airplane window" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/30 to-transparent" />
              <div className="absolute bottom-0 p-10">
                <p className="text-xs font-semibold tracking-[0.3em] text-gold font-display mb-3">{t("eligibility.eyebrow")}</p>
                <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white leading-tight" data-testid="eligibility-heading">
                  {t("eligibility.title")}
                </h2>
                <p className="mt-4 text-sm text-slate-300 leading-relaxed max-w-sm">
                  {t("eligibility.desc")}
                </p>
              </div>
            </div>
            <div className="lg:hidden">
              <p className="text-xs font-semibold tracking-[0.3em] text-gold font-display mb-3">{t("eligibility.eyebrow")}</p>
              <h2 className="font-display text-3xl font-extrabold text-white leading-tight" data-testid="eligibility-heading-mobile">
                {t("eligibility.title")}
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              {done ? (
                <div className="text-center py-12" data-testid="eligibility-success">
                  <CheckCircle2 className="h-14 w-14 text-gold mx-auto mb-5" />
                  <h3 className="font-display text-2xl font-bold text-white mb-3">{t("eligibility.successTitle")}</h3>
                  <p className="text-slate-400 max-w-sm mx-auto text-sm leading-relaxed">
                    {t("eligibility.successText")}
                  </p>
                  <p className="mt-5 text-[11px] text-slate-500 leading-relaxed max-w-sm mx-auto">{t("eligibility.disclaimer")}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="eligibility-checker-form">
                  <input required minLength={2} value={form.name} onChange={set("name")} placeholder={t("eligibility.name")} data-testid="eligibility-name-input" className="input-premium" />
                  <input required type="number" min={16} max={100} value={form.age} onChange={set("age")} placeholder={t("eligibility.age")} data-testid="eligibility-age-input" className="input-premium" />
                  <input required minLength={6} value={form.phone} onChange={set("phone")} placeholder={t("eligibility.phone")} data-testid="eligibility-phone-input" className="input-premium" />
                  <input required type="email" value={form.email} onChange={set("email")} placeholder={t("eligibility.email")} data-testid="eligibility-email-input" className="input-premium" />
                  <Select label={t("eligibility.education")} value={form.education} onChange={set("education")} options={EDUCATION_LEVELS} testid="eligibility-education-select" />
                  <input required value={form.occupation} onChange={set("occupation")} placeholder={t("eligibility.occupation")} data-testid="eligibility-occupation-input" className="input-premium" />
                  <Select label={t("eligibility.experience")} value={form.experience} onChange={set("experience")} options={EXPERIENCE_LEVELS} testid="eligibility-experience-select" />
                  <Select label={t("eligibility.country")} value={form.country} onChange={set("country")} options={[...COUNTRIES.map((c) => c.name), "Other"]} testid="eligibility-country-select" />
                  <Select label={t("eligibility.visaType")} value={form.visa_type} onChange={set("visa_type")} options={VISA_TYPES} testid="eligibility-visa-select" />
                  <Select label={t("eligibility.english")} value={form.english_status} onChange={set("english_status")} options={ENGLISH_STATUS} testid="eligibility-english-select" />
                  <textarea value={form.message} onChange={set("message")} placeholder={t("eligibility.message")} rows={3} data-testid="eligibility-message-input" className="input-premium sm:col-span-2 resize-none" />
                  <div className="sm:col-span-2 flex items-start gap-2.5 text-[11px] text-slate-500 leading-relaxed">
                    <AlertCircle className="h-4 w-4 shrink-0 text-gold/70 mt-0.5" />
                    <p data-testid="eligibility-disclaimer">{t("eligibility.disclaimer")}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="eligibility-submit-btn"
                      className="btn-gold w-full rounded-full py-4 text-sm font-bold tracking-wider font-display flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {loading ? t("eligibility.submitting") : t("eligibility.submit")} <ClipboardCheck className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default EligibilitySection;
